class PlaytimeSearch extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;
    this.lifecycle = new AbortController();
    const options = { signal: this.lifecycle.signal };
    this.input = this.querySelector('input[name="q"]');
    this.form = this.querySelector('form');
    this.list = this.querySelector('[role="listbox"]');
    this.status = this.querySelector('[data-search-status]');
    this.categories = [...(this.querySelector('[data-roads-search-index]')?.content.querySelectorAll('a') || [])];
    this.generation = 0;
    this.active = -1;
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', this.list.id);
    this.input.setAttribute('aria-expanded', 'false');
    this.input.addEventListener('input', () => this.schedule(), options);
    this.input.addEventListener('focus', () => this.schedule(), options);
    this.input.addEventListener('compositionstart', () => { this.composing = true; this.dismiss(); }, options);
    this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); }, options);
    this.input.addEventListener('keydown', event => this.keydown(event), options);
    this.scopeKey = this.predictiveContext();
    document.addEventListener('playtime:audience', () => {
      const scopeKey = this.predictiveContext();
      if (this.scopeKey === scopeKey) return;
      this.scopeKey = scopeKey;
      if (document.activeElement === this.input) this.schedule();
      else this.dismiss();
    }, options);
    this.addEventListener('focusout', event => {
      if (!this.contains(event.relatedTarget)) this.dismiss();
    }, options);
    this.form.addEventListener('submit', event => {
      if (this.composing) { event.preventDefault(); return; }
      event.preventDefault();
      this.dismiss();
      location.assign(this.resultsUrl(this.input.value.trim()).href);
    }, options);
  }
  audience() { return window.PlaytimeAudience?.read(location.href); }
  isStrict() {
    const s = this.audience();
    return Boolean(s?.explicit && s.mode === 'only' && s.audience !== 'all');
  }
  predictiveContext() {
    const s = this.audience();
    return s?.explicit ? [s.mode, ...(s.audiences || [s.audience])].join(':') : 'default';
  }
  scopedLink(value) {
    const url = new URL(value, location.href);
    const decorated = window.PlaytimeAudience?.link(url) || url;
    const here = new URL(location.href);
    if (!decorated.searchParams.has('view') && /^roads(?:-|$)/.test(here.searchParams.get('view') || '')) decorated.searchParams.set('view', 'roads');
    if (this.isStrict()) decorated.searchParams.set('pt_mode', 'only');
    return decorated;
  }
  resultsUrl(query) {
    const action = new URL(this.form.action, location.href), here = new URL(location.href);
    const preserve = this.form.hasAttribute('data-preserve-search') && action.pathname === here.pathname;
    const url = preserve ? new URL(here) : action;
    const context = new Set(['pt_aud','pt_mode','pt_hub','filter.p.m.custom.audience']);
    const data = new FormData(this.form);
    for (const key of new Set(data.keys())) url.searchParams.delete(key);
    for (const [key,value] of data) if (!context.has(key) && String(value).trim()) url.searchParams.append(key,value);
    for (const key of [...context,'pt_page','page','section_id','sections']) url.searchParams.delete(key);
    url.searchParams.set('q',query);url.searchParams.set('type','product');url.hash='';
    return this.scopedLink(url);
  }
  cancel() {
    this.generation += 1;
    clearTimeout(this.debounce);
    this.debounce = null;
    clearTimeout(this.timeout);
    this.request?.abort();
    this.request = null;
    this.input?.removeAttribute('aria-busy');
  }
  dismiss() {
    this.cancel();
    this.list.hidden = true;
    this.list.replaceChildren();
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.active = -1;
    this.status.textContent = '';
  }
  schedule() {
    this.dismiss();
    const query = this.input.value.trim();
    if (this.composing || query.length < 2) return;
    const count = this.renderSuggestions(query, []);
    if (this.isStrict()) {
      this.status.textContent = this.dataset.count.replace('[count]', String(count));
      return;
    }
    const generation = this.generation;
    this.debounce = setTimeout(() => this.suggest(query, generation), 180);
  }
  renderSuggestions(query, products) {
    const words = query.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const fragment = document.createDocumentFragment();
    const add = link => {
      const item = document.createElement('li');
      item.setAttribute('role', 'presentation');
      link.id = `${this.list.id}-${fragment.childElementCount}`;
      link.tabIndex = -1;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      item.append(link);
      fragment.append(item);
    };
    for (const category of this.categories) {
      if (!words || fragment.childElementCount === 3) break;
      const aliases = category.dataset.roadsAliases.split(',').map(alias => alias.replace(/[^a-z0-9]+/g, ' ').trim());
      if (aliases.includes('thong') && /\b(candy|sweets)\b/.test(words)) continue;
      if (!aliases.some(alias => alias.startsWith(words) || (' '+words+' ').includes(' '+alias+' '))) continue;
      const link = category.cloneNode(true);
      link.className = 'roads-search-hit';
      link.href = this.scopedLink(link.href).href;
      add(link);
    }
    const strict = this.isStrict();
    if (strict) {
      const link = document.createElement('a');link.href = this.resultsUrl(query).href;
      link.textContent = 'See results for “'+query+'”';add(link);
    }
    for (const product of strict ? [] : products.slice(0, 6)) {
      if (typeof product.url !== 'string' || typeof product.title !== 'string') continue;
      const target = new URL(product.url, location.origin);
      if (target.origin !== location.origin || !target.pathname.includes('/products/')) continue;
      const link = document.createElement('a');
      link.href = this.scopedLink(target).href;
      const title = document.createElement('span');
      title.textContent = product.title;
      link.append(title);
      if (typeof product.vendor === 'string' && product.vendor) {
        const vendor = document.createElement('small');
        vendor.textContent = product.vendor;
        link.append(vendor);
      }
      add(link);
    }
    const selected = this.active >= 0 ? this.list.querySelectorAll('[role="option"]')[this.active]?.href : null;
    this.list.replaceChildren(fragment);
    const options = [...this.list.querySelectorAll('[role="option"]')];
    this.active = selected ? options.findIndex(link => link.href === selected) : -1;
    this.input.removeAttribute('aria-activedescendant');
    if (this.active >= 0) {
      options[this.active].setAttribute('aria-selected','true');
      this.input.setAttribute('aria-activedescendant',options[this.active].id);
    }
    this.list.hidden = options.length === 0;
    this.input.setAttribute('aria-expanded', String(options.length > 0));
    return options.length;
  }
  async suggest(query, generation) {
    this.debounce = null;
    if (generation !== this.generation || document.activeElement !== this.input || this.isStrict()) return;
    const controller = new AbortController();
    this.request = controller;
    this.status.textContent = this.dataset.loading;
    this.input.setAttribute('aria-busy', 'true');
    let timedOut = false;
    this.timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 7000);
    try {
      const url = new URL(this.dataset.endpoint, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Predictive search unavailable');
      const data = await response.json();
      if (timedOut) throw new Error('Predictive search timed out');
      if (generation !== this.generation || this.input.value.trim() !== query || document.activeElement !== this.input) return;
      const products = data?.resources?.results?.products;
      if (!Array.isArray(products)) throw new Error('Invalid predictive response');
      const count = this.renderSuggestions(query, products);
      this.status.textContent = count ? this.dataset.count.replace('[count]', String(count)) : this.dataset.empty;
    } catch (error) {
      if (generation !== this.generation || document.activeElement !== this.input) return;
      if (error.name === 'AbortError' && !timedOut) return;
      const count = this.renderSuggestions(query, []);
      this.status.textContent = count ? this.dataset.count.replace('[count]', String(count)) : this.dataset.error;
    } finally {
      if (generation === this.generation) {
        clearTimeout(this.timeout);
        this.input.removeAttribute('aria-busy');
        this.request = null;
      }
    }
  }
  keydown(event) {
    if (event.isComposing || this.composing) return;
    if (event.key === 'Escape' && (!this.list.hidden || this.request || this.debounce || this.status.textContent)) {
      event.preventDefault();
      event.stopPropagation();
      this.dismiss();
      this.debounce = null;
      return;
    }
    if (this.list.hidden) return;
    const links = [...this.list.querySelectorAll('[role="option"]')];
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.active = this.active < 0 ? (step === 1 ? 0 : links.length - 1) : (this.active + step + links.length) % links.length;
      links.forEach((link, index) => link.setAttribute('aria-selected', String(index === this.active)));
      this.input.setAttribute('aria-activedescendant', links[this.active].id);
      links[this.active].scrollIntoView({ block: 'nearest', behavior: 'instant' });
    } else if (event.key === 'Enter' && this.active >= 0) {
      event.preventDefault();
      links[this.active].click();
    } else if (event.key === 'Tab') {
      this.dismiss();
    }
  }
  disconnectedCallback() {
    this.dismiss();
    this.lifecycle?.abort();
    this.lifecycle = null;
  }
}
class PlaytimeSearchDialog extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;
    this.lifecycle = new AbortController();
    const options = { signal: this.lifecycle.signal };
    this.dialog = this.querySelector('dialog');
    this.trigger = this.querySelector('[data-search-open]');
    if (typeof this.dialog.showModal !== 'function') return;
    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.trigger.setAttribute('aria-controls', this.dialog.id);
    this.trigger.addEventListener('click', event => {
      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.dialog.showModal();
      document.body.classList.add('search-open');
      this.dialog.querySelector('input[name="q"]').focus();
    }, options);
    this.querySelector('[data-search-close]').addEventListener('click', () => this.dialog.close(), options);
    this.dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        this.dialog.close();
      }
    }, options);
    this.dialog.addEventListener('close', () => {
      this.querySelector('playtime-search').dismiss();
      document.body.classList.remove('search-open');
      this.trigger.focus({ preventScroll: true });
    }, options);
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, options);
    window.addEventListener('pagehide', () => { if (this.dialog.open) this.dialog.close(); }, options);
  }
  disconnectedCallback() {
    if (this.dialog?.open) {
      this.dialog.close();
      document.body.classList.remove('search-open');
    }
    this.lifecycle?.abort();
    this.lifecycle = null;
  }
}
if (!customElements.get('playtime-search')) customElements.define('playtime-search', PlaytimeSearch);
if (!customElements.get('playtime-search-dialog')) customElements.define('playtime-search-dialog', PlaytimeSearchDialog);
